import { html } from 'lit';
import { Router } from '@lit-labs/router';
import type { ReactiveControllerHost } from 'lit';

// Side-effect imports: register the (non-lazy) page custom elements so the router can render them.
// The admin group is intentionally NOT imported here — it is code-split via the `/admin/*` `enter`.
import './pages/login-page.js';
import './pages/choose-method-page.js';
import './pages/totp-entry-page.js';
import './pages/passkey-login-page.js';
import './pages/totp-enroll-page.js';
import './pages/passkey-register-page.js';
import './pages/enrollment-gate-page.js';
import './pages/recovery-codes-page.js';
import './pages/forgot-password-page.js';
import './pages/reset-password-page.js';
import './pages/logout-page.js';
import './pages/launcher-page.js';
import './pages/security-page.js';
import './pages/no-access-page.js';

/**
 * Route table for the SSO SPA (SSO-FRONTEND-LIT.md §5.1).
 *
 * `@lit-labs/router` is a reactive controller built on the native URLPattern API. The route set is
 * small and mostly linear, so the table is flat. The admin group is code-split via a dynamic
 * `import()` in `enter` so the login bundle stays minimal.
 *
 * Labs risk: `@lit-labs/router` is pre-1.0; if it churns, fall back to a ~60-line hand-rolled
 * History + URLPattern router or a frozen `@vaadin/router` pin (see §7).
 */
export const makeRouter = (host: ReactiveControllerHost & HTMLElement): Router =>
  new Router(host, [
    { path: '/login', render: () => html`<login-page></login-page>` },
    { path: '/2fa/choose', render: () => html`<choose-method-page></choose-method-page>` },
    { path: '/2fa/totp', render: () => html`<totp-entry-page></totp-entry-page>` },
    { path: '/2fa/passkey', render: () => html`<passkey-login-page></passkey-login-page>` },
    { path: '/enroll/totp', render: () => html`<totp-enroll-page></totp-enroll-page>` },
    { path: '/enroll/passkey', render: () => html`<passkey-register-page></passkey-register-page>` },
    { path: '/enroll/gate', render: () => html`<enrollment-gate-page></enrollment-gate-page>` },
    { path: '/recovery-codes', render: () => html`<recovery-codes-page></recovery-codes-page>` },
    { path: '/forgot-password', render: () => html`<forgot-password-page></forgot-password-page>` },
    { path: '/reset-password', render: () => html`<reset-password-page></reset-password-page>` },
    // Activation / onboarding (first password) reuses the reset page in `activate` mode.
    { path: '/activate', render: () => html`<reset-password-page></reset-password-page>` },
    { path: '/logout', render: () => html`<logout-page></logout-page>` },
    { path: '/security', render: () => html`<security-page></security-page>` },
    { path: '/no-access', render: () => html`<no-access-page></no-access-page>` },
    // Lazy chunk: admin code is only loaded for role-gated users. Two patterns so both `/admin`
    // and `/admin/<sub>` match (URLPattern `/admin/*` does NOT match the bare `/admin`).
    {
      path: '/admin',
      enter: async () => {
        await import('./pages/admin/admin-shell.js');
        return true;
      },
      render: () => html`<admin-shell></admin-shell>`,
    },
    {
      path: '/admin/*',
      enter: async () => {
        await import('./pages/admin/admin-shell.js');
        return true;
      },
      render: () => html`<admin-shell></admin-shell>`,
    },
    // Launcher (D5) is the index route.
    { path: '/', render: () => html`<launcher-page></launcher-page>` },
  ]);

/**
 * Map a flow token to a client route path.
 *
 * Server `next` values: 'mfa' (existing 2FA → choose), 'enroll' (forced enrollment gate),
 * 'complete' (launcher). The remaining tokens are client-internal steps used by pages to route
 * between local screens (choose / totp / passkey / recovery-codes).
 */
export function pathForStep(next: string | undefined): string {
  switch (next) {
    case 'mfa':
    case 'choose':
      return '/2fa/choose';
    case 'totp':
      return '/2fa/totp';
    case 'passkey':
      return '/2fa/passkey';
    case 'enroll':
      return '/enroll/gate';
    case 'recovery':
      return '/recovery-codes';
    case 'complete':
    case 'launcher':
      return '/';
    default:
      return '/';
  }
}

/**
 * Client-side navigation that keeps `@lit-labs/router` in sync.
 * Pushes history then dispatches a popstate-like update the Router observes.
 */
export function navigate(path: string): void {
  const current = location.pathname + location.search;
  if (current !== path) {
    history.pushState({}, '', path);
    // @lit-labs/router listens on the navigation/popstate signal; dispatch to trigger a re-route.
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}
